'use client';

import React, { useState } from 'react';
import Modal from '../ui/Modal';
import Form, { FormItem } from '../ui/Form';
import { useForm } from 'react-hook-form';
import Input from '../ui/Input';
import { Button } from '../ui/Button';
import { useValidation } from '@/app/hooks/validation';
import { PhoneInput } from '../ui/PhoneInput';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createWorkspace } from '@/api/client';
import { getClientRoles, inviteUser } from '@/api/user';
import { useToast } from '@/app/hooks/use-toast';
import { getErrorMessage } from '@/app/lib/utils';

type AddClientModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type FormData = {
  clientName: string;
  clientWebsite: string;
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
};

const AddClientModal = ({ isOpen, onClose, onSuccess }: AddClientModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      clientName: '',
      clientWebsite: '',
      firstName: '',
      middleName: '',
      lastName: '',
      title: '',
      email: '',
      phone: '',
    },
  });

  const validation = useValidation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { mutateAsync: createClientMutation } = useMutation({
    mutationFn: createWorkspace,
  });

  const { mutateAsync: inviteUserMutation } = useMutation({
    mutationFn: (payload: any) => inviteUser(payload),
  });

  const handleSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      const contactName = [data.firstName, data.middleName, data.lastName]
        .filter(Boolean)
        .join(' ');
      const primaryContact = {
        name: contactName,
        phone: data.phone || undefined,
        email: data.email,
      };
      const clientResponse = await createClientMutation({
        name: data.clientName,
        website: data.clientWebsite,
        primary_contact: primaryContact,
        billing_contact: {
          name: primaryContact.name,
          phone: primaryContact.phone,
          email: primaryContact.email,
        },
      });

      const clientId = (clientResponse as any)?.id || (clientResponse as any)?.data?.id; // Standardize based on API response

      toast({
        title: 'Client created successfully',
        toastType: 'success',
      });

      // Refetch clients list early
      queryClient.invalidateQueries({ queryKey: ['clients'] });

      if (clientId) {
        const roles = await getClientRoles(clientId);
        const adminRoleId = roles.data.find(role => role.name === 'admin')?.id;

        await inviteUserMutation({
          client_ids: [clientId],
          first_name: data.firstName,
          last_name: data.lastName || undefined,
          middle_name: data.middleName || undefined,
          title: data.title || undefined,
          email: data.email,
          phone_number: data.phone || undefined,
          role_id: adminRoleId,
          type: 'external',
        });

        toast({
          title: 'Admin user invited successfully',
          toastType: 'success',
        });
      }

      form.reset();
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: getErrorMessage(e),
        toastType: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          form.reset();
          onClose();
        }
      }}
      title="Add new client"
      showClose={true}
      hasScroll={true}
      headerClassName="pt-5"
      bodyClassName="sm:max-w-[880px] w-full flex flex-col p-0 gap-0"
    >
      <Form form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex divide-x divide-border">
          <div className="flex-1 py-1 pb-5 pr-4 flex flex-col gap-6">
            <div className="flex gap-4">
              <FormItem
                label="Client Name"
                name="clientName"
                labelClassName="text-foreground"
                className="flex-1"
                rules={validation(['required'])}
                render={({ field }) => <Input {...field} placeholder="Client name" />}
              />
              <FormItem
                label="Website"
                name="clientWebsite"
                labelClassName="text-foreground"
                className="flex-1"
                rules={validation(['required', 'url'])}
                render={({ field }) => <Input {...field} placeholder="Website" />}
              />
            </div>

            <hr className="border-border -mx-5" />

            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-medium text-foreground">Primary Admin User</h3>
              <div className="flex gap-4">
                <FormItem
                  label="First Name"
                  name="firstName"
                  labelClassName="text-foreground"
                  className="flex-1"
                  rules={validation(['required'])}
                  render={({ field }) => <Input {...field} placeholder="First name" />}
                />
                <FormItem
                  label="Middle Name"
                  name="middleName"
                  labelClassName="text-foreground"
                  className="flex-1"
                  render={({ field }) => <Input {...field} placeholder="Middle name" />}
                />
                <FormItem
                  label="Last Name"
                  name="lastName"
                  labelClassName="text-foreground"
                  className="flex-1"
                  rules={validation(['required'])}
                  render={({ field }) => <Input {...field} placeholder="Last name" />}
                />
              </div>

              <div className="flex gap-4">
                <FormItem
                  label="Title"
                  name="title"
                  className="flex-1"
                  labelClassName="text-foreground"
                  render={({ field }) => <Input {...field} placeholder="Title" />}
                />

                <FormItem
                  label="Email"
                  name="email"
                  className="flex-1"
                  labelClassName="text-foreground"
                  rules={validation(['required', 'email'])}
                  render={({ field }) => <Input {...field} placeholder="Email" />}
                />

                <FormItem
                  label="Phone"
                  name="phone"
                  className="flex-1"
                  labelClassName="text-foreground"
                  render={({ field }) => (
                    <PhoneInput {...field} className="w-full" placeholder="Phone number" />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-3 px-5 -mx-3 border-t border-border">
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              form.reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !form.formState.isValid}
            loading={isSubmitting}
          >
            Add Client
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddClientModal;
